import { useCallback, useRef, useState } from "react";
import api from "@/lib/api";
import { normalizeOrder, normalizeUser } from "@/components/admin/adminHelpers";

const ADMIN_REQUEST_TIMEOUT = 10000;

const unwrapData = (response, fallback) => {
  const data = response?.data ?? fallback;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return data ?? fallback;
};

const requestOrFallback = async (path, fallback) => {
  try {
    const response = await api.get(path, { timeout: ADMIN_REQUEST_TIMEOUT });
    return unwrapData(response, fallback);
  } catch (error) {
    console.error(`[admin] ${path} failed`, error);
    return fallback;
  }
};

const useAdminData = () => {
  const [orders, setOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const dataRef = useRef({
    orders: [],
    subscriptions: [],
    users: [],
    notifications: [],
    adminStats: null,
  });

  const applyData = useCallback((data) => {
    const nextOrders = (Array.isArray(data.orders) ? data.orders : []).map(normalizeOrder);
    const nextUsers = (Array.isArray(data.users) ? data.users : []).map(normalizeUser);
    const nextSubscriptions = Array.isArray(data.subscriptions) ? data.subscriptions : [];
    const nextNotifications = Array.isArray(data.notifications) ? data.notifications : [];
    const nextStats = data.adminStats !== undefined ? data.adminStats || null : dataRef.current.adminStats;

    dataRef.current = {
      orders: nextOrders,
      users: nextUsers,
      subscriptions: nextSubscriptions,
      notifications: nextNotifications,
      adminStats: nextStats,
    };

    setOrders(nextOrders);
    setSubscriptions(nextSubscriptions);
    setUsers(nextUsers);
    setNotifications(nextNotifications);
    if (data.adminStats !== undefined) setAdminStats(nextStats);

    return dataRef.current;
  }, []);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);

    try {
      const current = dataRef.current;
      const [nextOrders, nextSubscriptions, nextUsers, nextNotifications] = await Promise.all([
        requestOrFallback("/admin/orders", current.orders),
        requestOrFallback("/admin/subscriptions", current.subscriptions),
        requestOrFallback("/admin/users", current.users),
        requestOrFallback("/notifications", current.notifications),
      ]);

      const applied = applyData({
        orders: nextOrders,
        subscriptions: nextSubscriptions,
        users: nextUsers,
        notifications: nextNotifications,
      });

      requestOrFallback("/admin/stats", dataRef.current.adminStats)
        .then((nextStats) => {
          dataRef.current = { ...dataRef.current, adminStats: nextStats || null };
          setAdminStats(nextStats || null);
        })
        .catch((error) => {
          console.error("[admin] stats background refresh failed", error);
        });

      return applied;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [applyData]);

  const updateNotifications = useCallback((updater) => {
    setNotifications((current) => {
      const nextNotifications = typeof updater === "function" ? updater(current) : updater;
      dataRef.current = { ...dataRef.current, notifications: nextNotifications };
      return nextNotifications;
    });
  }, []);

  return {
    orders,
    subscriptions,
    users,
    notifications,
    adminStats,
    loading,
    setNotifications: updateNotifications,
    fetchData,
  };
};

export default useAdminData;
